import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaUniversity,
  FaGraduationCap,
  FaCalendarAlt,
  FaInfoCircle,
  FaGlobe,
  FaMapMarkedAlt,
  FaArrowLeft,
  FaArrowRight,
  FaLock
} from "react-icons/fa";

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const FormField = ({
  type = "text",
  label,
  name,
  value,
  onChange,
  placeholder,
  options = [],
  readOnly = false,
  icon = null,
  disabled = false,
  required = true,
}) => (
  <motion.div variants={fieldVariants} className="mb-4">
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}{" "}
      {required && !readOnly && !disabled && (
        <span className="text-red-500">*</span>
      )}
    </label>
    <div className="relative flex items-center">
      {icon && (
        <span className="absolute left-3 text-gray-400 z-10 text-sm">{icon}</span>
      )}
      {type === "select" ? (
        <select
          id={name}
          name={name}
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          className={`w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm ${
            icon ? "pl-9" : "pl-3"
          } shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
            disabled
              ? "bg-gray-100 cursor-not-allowed text-gray-500"
              : "hover:border-gray-400"
          }`}
        >
          <option value="">Select {label}</option>
          {options.map((opt) =>
            typeof opt === "object" ? (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ) : (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )
          )}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          name={name}
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          readOnly={readOnly}
          disabled={disabled}
          className={`w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm ${
            icon ? "pl-9" : "pl-3"
          } shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
            readOnly || disabled
              ? "bg-gray-100 cursor-not-allowed text-gray-500"
              : "hover:border-gray-400"
          }`}
        />
      )}
      {disabled && (
        <div className="absolute right-3 text-gray-400">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
        </div>
      )}
    </div>
    
  </motion.div>
);

// Button Components
const PreviousButton = ({ onClick, disabled = false }) => (
  <motion.button
    whileHover={{ scale: disabled ? 1 : 1.02 }}
    whileTap={{ scale: disabled ? 1 : 0.98 }}
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm flex-1"
  >
    <FaArrowLeft className="text-xs" />
    Previous
  </motion.button>
);

const NextButton = ({ onClick, disabled = false, loading = false, isUpdate = false }) => (
  <motion.button
    whileHover={{ scale: disabled ? 1 : 1.02 }}
    whileTap={{ scale: disabled ? 1 : 0.98 }}
    type="submit"
    onClick={onClick}
    disabled={disabled || loading}
    className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm flex-1"
  >
    {loading ? (
      <>
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
        Processing...
      </>
    ) : (
      <>
        {isUpdate ? "Update & Next" : "Next"}
        <FaArrowRight className="text-xs" />
      </>
    )}
  </motion.button>
);

// Admin Form Field Wrapper
const AdminFormField = ({ isAdmin, ...props }) => {
  const fieldProps = {
    ...props,
    readOnly: !isAdmin || props.readOnly,
    disabled: !isAdmin || props.disabled,
  };

  return <FormField {...fieldProps} />;
};

// Admin Lock Indicator
const AdminLockIndicator = ({ isAdmin }) => {
  if (isAdmin) return null;
  
  return (
    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex items-center text-yellow-700">
        <FaLock className="mr-2" />
        <span className="text-sm font-medium">
          View-only mode: Only administrators can edit these fields
        </span>
      </div>
    </div>
  );
};

export default function RegisterPage1({
  student_id,
  initialData,
  formData = {},
  onNext,
  onDataChange,
  onPrevious,
}) {
  const navigate = useNavigate();
  const isInitialized = useRef(false);
  const hasMappedInitialData = useRef(false);

  // Add state for user role
  const [userRole, setUserRole] = useState("user");
  const [isAdmin, setIsAdmin] = useState(false);

  const [localFormData, setLocalFormData] = useState({
    group: "",
    groupId: "",
    residency: "",
    city: "",
    country: "",
    university: "",
    programme: "",
    intake: "",
    deliveryPattern: "",
    entryLevel: "",
    courseFee: "",
    firstYearFee: "",
    loanFunded: "",
    apl: false,
    heardFrom: "",
    euSettlementCode: "",
    programmeGroup: "",
    programmeField: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isLoadingIntakes, setIsLoadingIntakes] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingProgrammes, setIsLoadingProgrammes] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Options state
  const [residencyOptions, setResidencyOptions] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [deliveryPatterns, setDeliveryPatterns] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [programmeGroups, setProgrammeGroups] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [programmeFields, setProgrammeFields] = useState([]);
  const [groups, setGroups] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [InternationalCities, InternationalsetCities] = useState([]);

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = () => {
      const userData = localStorage.getItem("user");

      if (!userData) {
        setUserRole("user");
        setIsAdmin(false);
        return;
      }

      try {
        const parsedData = JSON.parse(userData);
        const role = (parsedData.role || "user").toLowerCase();

        setUserRole(role);
        setIsAdmin(role === "admin");

      } catch (error) {
        console.error("Error parsing user data:", error);
        setUserRole("user");
        setIsAdmin(false);
      }
    };

    checkUserRole();
  }, []);

  // Improved data mapping function
  const mapApiDataToForm = useCallback((apiData) => {
    if (!apiData) {
      return {};
    }

    const courseDetail = apiData.course_detail || {};
    const user = apiData.user || {};
    const correspondenceAddress = apiData.correspondence_address || {};
    const formData = apiData.form_data || {};

    const heardFromMapping = {
      Friend: "Friend",
      Facebook: "Facebook",
      Instagram: "Instagram",
      Twitter: "Twitter",
      LinkedIn: "LinkedIn",
      TikTok: "TikTok",
      Snapchat: "Snapchat",
      Website: "Website",
      Advertisement: "Advertisement",
      "Career Fair": "Career Fair",
      Other: "Other",
    };

    const mappedData = {
      residency:
        (formData.residency || courseDetail.residency_status)?.toString() || "",
      city: (user.city_id || correspondenceAddress.city)?.toString() || "",
      country:
        (formData.country || correspondenceAddress.country)?.toString() || "",
      university:
        (formData.university || courseDetail.university)?.toString() || "",
      programmeGroup:
        (formData.programmeGroup || courseDetail.programme_group)?.toString() ||
        "",
      programme:
        (formData.programme || courseDetail.programme)?.toString() || "",
      programmeField:
        (formData.programme || courseDetail.programme)?.toString() || "",
      group: (formData.groupId || courseDetail.group_id)?.toString() || "",
      groupId: (formData.groupId || courseDetail.group_id)?.toString() || "",
      intake: (formData.intake || courseDetail.intake)?.toString() || "",
      deliveryPattern:
        (
          formData.deliveryPattern || courseDetail.delivery_pattern
        )?.toString() || "",
      entryLevel: formData.entryLevel || courseDetail.entry_level || "",
      courseFee: formData.courseFee || courseDetail.course_fee || "",
      firstYearFee: formData.firstYearFee || courseDetail.first_year_fee || "",
      loanFunded: formData.loanFunded || courseDetail.loan_funded || "",
      apl:
        formData.apl === true ||
        courseDetail.advanced_entry_apply === "1" ||
        courseDetail.advanced_entry_apply === true,
      heardFrom:
        heardFromMapping[formData.heardFrom || courseDetail.hear_about_us] ||
        formData.heardFrom ||
        courseDetail.hear_about_us ||
        "",
      euSettlementCode:
        formData.euSettlementCode || courseDetail.eu_settlement_code || "",
    };

    // Clean up values - convert "0" strings to empty strings for dropdowns
    Object.keys(mappedData).forEach((key) => {
      if (mappedData[key] === "0" || mappedData[key] === 0) {
        mappedData[key] = "";
      }
    });

    return mappedData;
  }, []);

  // Function to auto-fill entry level when programme is selected
  const autoFillEntryLevel = useCallback(
    (programmeId) => {
      if (!programmeId) return;

      const selectedProgramme = programmes.find(
        (p) => p.programme_id?.toString() === programmeId.toString()
      );

      if (selectedProgramme && selectedProgramme.entry_level) {
        setLocalFormData((prev) => ({
          ...prev,
          entryLevel: selectedProgramme.entry_level,
        }));
      }
    },
    [programmes]
  );

  // API fetch functions
  const fetchUniversitiesByProgrammeAndCity = useCallback(
    async (programmeId, cityId) => {
      if (!programmeId || !cityId) {
        setFilteredUniversities([]);
        return;
      }

      try {
        setIsLoadingUniversities(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/universities-by-programme-and-city/${programmeId}/${cityId}`
        );

        if (!res.ok) throw new Error("Failed to fetch universities");

        const data = await res.json();
        setFilteredUniversities(data.universities || []);
      } catch (err) {
        console.error(
          "Failed to fetch universities by programme and city:",
          err
        );
        setFilteredUniversities([]);
      } finally {
        setIsLoadingUniversities(false);
      }
    },
    []
  );

  const fetchIntakesByProgramme = useCallback(async (programmeId) => {
    if (!programmeId) {
      setIntakes([]);
      return;
    }

    try {
      setIsLoadingIntakes(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-programme-intakes/${programmeId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch intakes");

      const data = await res.json();
      const intakeData = Array.isArray(data) ? data : data?.intakes || [];
      setIntakes(intakeData);
    } catch (err) {
      console.error("Failed to fetch intakes by programme:", err);
      setIntakes([]);
    } finally {
      setIsLoadingIntakes(false);
    }
  }, []);

  const fetchCitiesByCountry = useCallback(async (countryId) => {
    if (!countryId) {
      InternationalsetCities([]);
      return;
    }

    try {
      setIsLoadingCities(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/c_cities/${countryId}`
      );

      if (!res.ok) throw new Error("Failed to fetch cities");

      const data = await res.json();
      InternationalsetCities(data?.cities || []);
    } catch (err) {
      console.error("Failed to fetch cities by country:", err);
      InternationalsetCities([]);
    } finally {
      setIsLoadingCities(false);
    }
  }, []);

  const fetchProgrammesByGroup = useCallback(async (groupId) => {
    if (!groupId) {
      setProgrammes([]);
      setProgrammeFields([]);
      return;
    }

    try {
      let city = null;
      let pg = null;
      let residency = null;

      const storedData = localStorage.getItem("register_1");

      if (storedData) {
        const parsedData = JSON.parse(storedData);

        if (parsedData.programmeGroup) {
          pg = parsedData.programmeGroup;
          city = parsedData.city;
          residency = parsedData.residency;
        }

        if (Array.isArray(parsedData) && parsedData[0]?.programmeGroup) {
          pg = parsedData[0].programmeGroup;
          city = parsedData[0].city;
          residency = parsedData[0].residency;
        }
      }

      if (!city || !pg || !residency) {
        console.warn(
          "Missing city, programmeGroup, or residency in stored data"
        );
        setProgrammes([]);
        setProgrammeFields([]);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/programmes-by-groupp/${groupId}/${pg}/${city}/${residency}`
      );

      if (!res.ok)
        throw new Error(`Failed to fetch programmes: ${res.statusText}`);

      const data = await res.json();

      if (Array.isArray(data.programmes) && data.programmes.length > 0) {
        setProgrammes(data.programmes);

        const fields = data.programmes.map((p) => ({
          value: p.programme_id.toString(),
          label: p.programme,
        }));

        setProgrammeFields(fields);
      } else {
        setProgrammes([]);
        setProgrammeFields([
          { value: "", label: "No programmes found for this group" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching programmes by group:", error);
      setProgrammes([]);
      setProgrammeFields([
        { value: "", label: "No programmes found for this group" },
      ]);
    }
  }, []);

  const fetchGroupsByProgrammeGroup = useCallback(async (programmeGroupId) => {
    if (!programmeGroupId) {
      setGroups([]);
      return;
    }

    try {
      setIsLoadingGroups(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/groups-by-programme-group/${programmeGroupId}`
      );
      if (!res.ok) throw new Error("Failed to fetch groups");
      const data = await res.json();
      setGroups(Array.isArray(data.groups) ? data.groups : []);
    } catch (err) {
      console.error("Error fetching groups by programme group:", err);
      setGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  }, []);

  // Load form options first
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const [formOptionsRes, programmeGroupsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/form-options`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programme_groups`),
        ]);

        const formOptions = await formOptionsRes.json();
        const programmeGroupsData = await programmeGroupsRes.json();

        setResidencyOptions(formOptions.residencyStatuses || []);
        setDeliveryPatterns(formOptions.deliveryPattern || []);
        setCities(formOptions.cities || []);
        setCountries(formOptions.countries || []);
        setProgrammeGroups(programmeGroupsData || []);
      } catch (err) {
        console.error("Error loading form options:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const fetchDeliveryPatternsByProgramme = useCallback(async (programmeId) => {
    if (!programmeId) {
      setDeliveryPatterns([]);
      return;
    }

    try {
      let pg = programmeId;

      if (!pg) {
        const storedData = localStorage.getItem("register_step1");
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          if (parsedData.programme) {
            pg = parsedData.programme;
          }
          if (Array.isArray(parsedData) && parsedData[0]?.programme) {
            pg = parsedData[0].programme;
          }
        }
      }

      if (!pg) {
        setDeliveryPatterns([]);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/delivery-patterns/${pg}`
      );

      if (!res.ok) throw new Error("Failed to fetch delivery patterns");

      const data = await res.json();
      setDeliveryPatterns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch delivery patterns:", err);
      setDeliveryPatterns([]);
    }
  }, []);

  // Initialize form data after options are loaded
  useEffect(() => {
    if (isLoading || isInitialized.current) return;

    if (
      cities.length === 0 ||
      residencyOptions.length === 0 ||
      countries.length === 0
    )
      return;

    let newFormData = { ...localFormData };
    let shouldUpdate = false;

    if (formData && Object.keys(formData).length > 0) {
      newFormData = { ...newFormData, ...formData };
      shouldUpdate = true;
    }

    if (initialData && !hasMappedInitialData.current) {
      const mappedData = mapApiDataToForm(initialData);
      if (Object.keys(mappedData).length > 0) {
        newFormData = { ...newFormData, ...mappedData };
        shouldUpdate = true;
        hasMappedInitialData.current = true;

        if (mappedData.entryLevel) {
          newFormData.entryLevel = mappedData.entryLevel;
        }
      }
    }

    if (shouldUpdate) {
      setLocalFormData(newFormData);
      isInitialized.current = true;
    }
  }, [
    isLoading,
    initialData,
    formData,
    mapApiDataToForm,
    cities,
    countries,
    residencyOptions,
  ]);

  // Auto-fill entry level when programme is selected
  useEffect(() => {
    if (localFormData.programmeField && isInitialized.current) {
      autoFillEntryLevel(localFormData.programmeField);
    }
  }, [localFormData.programmeField, autoFillEntryLevel]);

  useEffect(() => {
    if (localFormData.programmeField && isInitialized.current) {
      fetchDeliveryPatternsByProgramme(localFormData.programmeField);
    }
  }, [localFormData.programmeField, fetchDeliveryPatternsByProgramme]);

  // Re-attempt mapping when cities/countries become available (fallback)
  useEffect(() => {
    if (
      !isInitialized.current &&
      !isLoading &&
      cities.length > 0 &&
      countries.length > 0 &&
      initialData &&
      !hasMappedInitialData.current
    ) {
      const mappedData = mapApiDataToForm(initialData);
      if (
        (mappedData.city && !localFormData.city) ||
        (mappedData.country && !localFormData.country)
      ) {
        setLocalFormData((prev) => ({
          ...prev,
          city: mappedData.city || prev.city,
          country: mappedData.country || prev.country,
          entryLevel: mappedData.entryLevel || prev.entryLevel,
        }));
      }
    }
  }, [
    cities,
    countries,
    initialData,
    isLoading,
    mapApiDataToForm,
    localFormData.city,
    localFormData.country,
  ]);

  // Save to localStorage whenever localFormData changes
  useEffect(() => {
    if (typeof window !== "undefined" && isInitialized.current) {
      localStorage.setItem("register_1", JSON.stringify(localFormData));

      if (onDataChange) {
        onDataChange(localFormData);
      }
    }
  }, [localFormData]); // ✅ FIXED: Removed onDataChange from deps to prevent infinite loop

  // Handle cities fetching based on country selection for international students
  useEffect(() => {
    const isInternationalStudent = localFormData.residency === "31";

    if (isInternationalStudent && localFormData.country) {
      fetchCitiesByCountry(localFormData.country);
    } else if (!isInternationalStudent && localFormData.residency) {
      const loadAllCities = async () => {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/form-options`
          );
          const formOptions = await res.json();
          setCities(formOptions.cities || []);
        } catch (err) {
          console.error("Error loading cities:", err);
        }
      };
      loadAllCities();
    }
  }, [localFormData.country, localFormData.residency, fetchCitiesByCountry]);

  // Handle dependent data loading
  useEffect(() => {
    if (localFormData.programmeGroup && isInitialized.current) {
      fetchGroupsByProgrammeGroup(localFormData.programmeGroup);
    }
  }, [localFormData.programmeGroup, fetchGroupsByProgrammeGroup]);

  useEffect(() => {
    if (
      (localFormData.groupId || localFormData.group) &&
      isInitialized.current
    ) {
      const groupId = localFormData.groupId || localFormData.group;
      fetchProgrammesByGroup(groupId);
    }
  }, [localFormData.groupId, localFormData.group, fetchProgrammesByGroup]);

  useEffect(() => {
    if (
      localFormData.programme &&
      localFormData.city &&
      isInitialized.current
    ) {
      fetchUniversitiesByProgrammeAndCity(
        localFormData.programme,
        localFormData.city
      );
    }
  }, [
    localFormData.programme,
    localFormData.city,
    fetchUniversitiesByProgrammeAndCity,
  ]);

  useEffect(() => {
    if (localFormData.programme && isInitialized.current) {
      fetchIntakesByProgramme(localFormData.programme);
    }
  }, [localFormData.programme, fetchIntakesByProgramme]);

  // Modified handleChange to prevent changes for non-admin users
  const handleChange = (e) => {
    // If not admin, prevent changes
    if (!isAdmin) {
      return;
    }

    const { name, value, type, checked } = e.target;

    setLocalFormData((prevData) => {
      let updatedData = { ...prevData };

      if (name === "group") {
        const selectedGroup = groups.find((g) => g.id.toString() === value);
        updatedData = {
          ...updatedData,
          group: value,
          groupId: selectedGroup ? selectedGroup.id.toString() : "",
          programmeField: "",
          university: "",
          programme: "",
          intake: "",
          courseFee: "",
          firstYearFee: "",
          entryLevel: "",
        };
      } else if (name === "programmeField") {
        updatedData = {
          ...updatedData,
          programmeField: value,
          programme: value,
          university: "",
          intake: "",
          courseFee: "",
          firstYearFee: "",
          entryLevel: "",
        };

        const selectedProgramme = programmes.find(
          (p) => p.programme_id?.toString() === value.toString()
        );
        if (selectedProgramme && selectedProgramme.entry_level) {
          updatedData.entryLevel = selectedProgramme.entry_level;
        }
      } else {
        updatedData = {
          ...updatedData,
          [name]: type === "checkbox" ? checked : value,
        };

        if (name === "residency") {
          updatedData = {
            ...updatedData,
            city: "",
            country: "",
            university: "",
            programme: "",
            intake: "",
            courseFee: "",
            firstYearFee: "",
            entryLevel: "",
            euSettlementCode: ["2", "3"].includes(value)
              ? updatedData.euSettlementCode
              : "",
            programmeGroup: "",
            programmeField: "",
            group: "",
            groupId: "",
          };
        } else if (name === "country") {
          updatedData = {
            ...updatedData,
            city: "",
            university: "",
          };
        } else if (name === "programmeGroup") {
          updatedData = {
            ...updatedData,
            programmeField: "",
            university: "",
            programme: "",
            intake: "",
            courseFee: "",
            firstYearFee: "",
            entryLevel: "",
            group: "",
            groupId: "",
          };
        } else if (name === "city") {
          updatedData = {
            ...updatedData,
            university: "",
          };
        } else if (name === "university") {
          updatedData = {
            ...updatedData,
            intake: "",
          };
        }
      }

      return updatedData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation - Note: Only admin users can fix validation errors
    const requiredFields = [
      "residency",
      "programmeGroup",
      "groupId",
      "programmeField",
      "university",
      "intake",
      "deliveryPattern",
      "loanFunded",
    ];

    const missingFields = requiredFields.filter(
      (field) => !localFormData[field]
    );

    // Check EU settlement code for specific residency types
    if (
      ["2", "3"].includes(localFormData.residency) &&
      !localFormData.euSettlementCode
    ) {
      missingFields.push("euSettlementCode");
    }

    // Check city and country for specific residency types
    const isInternationalStudent = localFormData.residency === "31";
    const residenciesRequiringCity = ["1", "2", "3", "4"];

    if (isInternationalStudent) {
      if (!localFormData.country) missingFields.push("country");
      if (!localFormData.city) missingFields.push("city");
    } else if (
      residenciesRequiringCity.includes(localFormData.residency) &&
      !localFormData.city
    ) {
      missingFields.push("city");
    }

    if (missingFields.length > 0) {
      if (isAdmin) {
        alert(
          `Please fill in the following required fields: ${missingFields.join(
            ", "
          )}`
        );
      } else {
        alert(
          `Some required fields are missing: ${missingFields.join(
            ", "
          )}. Please contact an administrator to update this information.`
        );
      }
      return;
    }

    const submitData = {
      ...localFormData,
      ...(student_id && { student_id }),
    };

    localStorage.setItem("register_1", JSON.stringify(submitData));
    onNext(submitData);
  };

  const showSettlementCode = ["28", "29"].includes(localFormData.residency);
  const isInternationalStudent = localFormData.residency === "31";
  const showCityField = ["1", "28", "27", "29"].includes(localFormData.residency);

  // Get cities based on student type
  const getCityOptions = () => {
    if (isInternationalStudent) {
      return InternationalCities.map((c) => ({
        value: c.id.toString(),
        label: c.city_name,
      }));
    } else {
      return cities.map((c) => ({
        value: c.id.toString(),
        label: c.city_name,
      }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          <span className="text-gray-600 text-lg">Loading form options...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-3 sm:p-4 md:p-6">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Course & University Selection
                {!isAdmin && (
                  <span className="ml-3 text-sm bg-yellow-500 text-white px-2 py-1 rounded">
                    View Only
                  </span>
                )}
              </h1>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[75vh] p-4 sm:p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Add admin lock indicator */}
            <AdminLockIndicator isAdmin={isAdmin} />
            
            <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <FaUniversity className="text-blue-600 text-lg" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-800">
                  Course Selection
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="Residency"
                  name="residency"
                  value={localFormData.residency}
                  onChange={handleChange}
                  options={residencyOptions.map((r) => ({
                    value: r.id.toString(),
                    label: r.residency,
                  }))}
                  icon={<FaGlobe />}
                />

                {showSettlementCode && (
                  <AdminFormField
                    isAdmin={isAdmin}
                    type="text"
                    label="EU Settlement Code"
                    name="euSettlementCode"
                    value={localFormData.euSettlementCode}
                    onChange={handleChange}
                    placeholder="Enter your EU settlement code"
                    icon={<FaInfoCircle />}
                  />
                )}

                {isInternationalStudent && (
                  <>
                    <AdminFormField
                      isAdmin={isAdmin}
                      type="select"
                      label="Country"
                      name="country"
                      value={localFormData.country}
                      onChange={handleChange}
                      options={countries.map((c) => ({
                        value: c.id.toString(),
                        label: c.nicename,
                      }))}
                      icon={<FaGlobe />}
                    />
                    <AdminFormField
                      isAdmin={isAdmin}
                      type="select"
                      label="City"
                      name="city"
                      value={localFormData.city}
                      onChange={handleChange}
                      options={getCityOptions()}
                      icon={<FaMapMarkedAlt />}
                      disabled={!localFormData.country || isLoadingCities || !isAdmin}
                    />
                  </>
                )}

                {showCityField && !isInternationalStudent && (
                  <AdminFormField
                    isAdmin={isAdmin}
                    type="select"
                    label="City"
                    name="city"
                    value={localFormData.city}
                    onChange={handleChange}
                    options={getCityOptions()}
                    icon={<FaMapMarkedAlt />}
                  />
                )}

                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="Level"
                  name="programmeGroup"
                  value={localFormData.programmeGroup}
                  onChange={handleChange}
                  options={programmeGroups.map((p) => ({
                    value: p.id.toString(),
                    label: p.name,
                  }))}
                  icon={<FaGraduationCap />}
                />

                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="Field Group"
                  name="group"
                  value={localFormData.group}
                  onChange={handleChange}
                  options={groups.map((g) => ({
                    value: g.id.toString(),
                    label: g.group_name,
                  }))}
                  icon={<FaGraduationCap />}
                  disabled={!localFormData.programmeGroup || isLoadingGroups || !isAdmin}
                />

                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="Programme Field"
                  name="programmeField"
                  value={localFormData.programmeField}
                  onChange={handleChange}
                  options={programmeFields}
                  icon={<FaGraduationCap />}
                  disabled={!localFormData.groupId || isLoadingProgrammes || !isAdmin}
                />

                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="University"
                  name="university"
                  value={localFormData.university}
                  onChange={handleChange}
                  options={filteredUniversities.map((uni) => ({
                    value: uni.id.toString(),
                    label: uni.university_name,
                  }))}
                  icon={<FaUniversity />}
                  disabled={
                    !localFormData.programme ||
                    !localFormData.city ||
                    isLoadingUniversities ||
                    !isAdmin
                  }
                />

                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="Intake"
                  name="intake"
                  value={localFormData.intake || ""}
                  onChange={handleChange}
                  options={intakes.map((intake) => ({
                    value: intake.id.toString(),
                    label: intake.intake,
                  }))}
                  icon={<FaCalendarAlt />}
                  disabled={!localFormData.programme || isLoadingIntakes || !isAdmin}
                />

                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="Delivery Pattern"
                  name="deliveryPattern"
                  value={localFormData.deliveryPattern || ""}
                  onChange={handleChange}
                  options={
                    deliveryPatterns.length > 0
                      ? deliveryPatterns.map((d) => ({
                          value: d.id.toString(),
                          label: d.pattern,
                        }))
                      : [{ value: "", label: "No delivery patterns available" }]
                  }
                  icon={<FaInfoCircle />}
                />

                <AdminFormField
                  isAdmin={isAdmin}
                  type="text"
                  label="Entry Level"
                  name="entryLevel"
                  value={localFormData.entryLevel}
                  onChange={handleChange}
                  placeholder="Auto-filled from programme selection"
                  icon={<FaInfoCircle />}
                  readOnly={true}
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                  <FaInfoCircle className="text-green-600 text-lg" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-800">
                  Additional Information
                </h3>
              </div>
              <div className="space-y-3 sm:space-y-4">
                <motion.div variants={fieldVariants} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Will your fees be funded by Student Loan Company?{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    {["Yes", "No"].map((option) => (
                      <label
                        key={option}
                        className={`flex items-center space-x-2 ${
                          isAdmin ? 'cursor-pointer' : 'cursor-not-allowed'
                        }`}
                      >
                        <div className="relative">
                          <input
                            type="radio"
                            name="loanFunded"
                            value={option}
                            onChange={handleChange}
                            checked={localFormData.loanFunded === option}
                            className="sr-only"
                            disabled={!isAdmin}
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              localFormData.loanFunded === option
                                ? "border-blue-600"
                                : "border-gray-300"
                            } ${!isAdmin ? 'opacity-50' : ''}`}
                          >
                            {localFormData.loanFunded === option && (
                              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                            )}
                          </div>
                        </div>
                        <span className="text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  variants={fieldVariants}
                  className="flex items-start"
                >
                  <div className="flex items-center h-5 mt-1">
                    <input
                      id="apl"
                      type="checkbox"
                      name="apl"
                      checked={localFormData.apl}
                      onChange={handleChange}
                      className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                        !isAdmin ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={!isAdmin}
                    />
                  </div>
                  <label htmlFor="apl" className="ml-3 text-sm text-gray-700">
                    Accreditation of prior learning (APL) relates to learning
                    from the past that can be credited against your desired
                    qualification.
                  </label>
                </motion.div>

                <AdminFormField
                  isAdmin={isAdmin}
                  type="select"
                  label="How did you hear about us?"
                  name="heardFrom"
                  value={localFormData.heardFrom}
                  onChange={handleChange}
                  options={[
                    "Friend/Family",
                    "Facebook",
                    "Instagram",
                    "Twitter",
                    "LinkedIn",
                    "TikTok",
                    "Snapchat",
                    "University Website",
                    "Advertisement",
                    "Career Fair",
                    "Search Engine",
                    "Other",
                  ]}
                  icon={<FaInfoCircle />}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6">
              <button
                type="button"
                disabled
                className="px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-400 rounded-lg font-medium text-gray-500 bg-gray-100 flex-1 text-sm opacity-50 cursor-not-allowed transition-colors duration-300"
              >
                Previous
              </button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg font-medium text-white shadow-md hover:shadow-lg hover:opacity-95 transition-all duration-300 flex-1 text-sm"
              >
                {student_id ? "Update & Next" : "Next"}
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}